/**
 * Commodity Expiry Page (KAN-300 Phase 4)
 *
 * Shows matured commodity trades and allows manual settlement price entry
 * and fixing trigger. Follows the FX ForwardMaturity page pattern:
 *   - Header: title + inline settlement price inputs + Run Fixing button
 *   - Main panel: CommodityTradesGrid showing matured trades with filters
 *
 * Replaces the old CommodityExpiryUpload.tsx (CSV upload approach).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../locales/i18n';
import { useAuth } from '../../../components/auth/AuthContext';
import CommodityTradesGrid from '../../../components/grids/CommodityTradesGrid';
import SystemDateDisplay from '../../../components/common/SystemDateDisplay';
import Toast from '../../../components/common/Toast';
import { useSystemDate } from '../../../hooks/useSystemDate';
import { commodityFixingService, MaturedTradeGroup } from '../../../services/api/commodityFixingService';
import { commodityValuationService } from '../../../services/api/commodityValuationService';
import { parseAmountInput } from '../../../utils/amountFormatter';
import '../../../styles/FilterButtons.css';
import './CommodityExpiry.css';

// ── Helpers ──

/** Allow digits and the locale decimal separator, capped at maxDecimals. */
const sanitizeDecimalInput = (value: string, maxDecimals: number): string => {
  const lang = i18n.language?.substring(0, 2);
  const decimalSep = (lang === 'es' || lang === 'pt') ? ',' : '.';
  const otherSep = decimalSep === ',' ? '.' : ',';

  const allowedChars = new RegExp(`[^0-9\\${decimalSep}]`, 'g');
  let clean = value.replace(otherSep, decimalSep).replace(allowedChars, '');

  const parts = clean.split(decimalSep);
  if (parts.length > 2) clean = parts[0] + decimalSep + parts.slice(1).join('');

  const sepIdx = clean.indexOf(decimalSep);
  if (sepIdx !== -1 && clean.length - sepIdx - 1 > maxDecimals) {
    clean = clean.substring(0, sepIdx + 1 + maxDecimals);
  }
  return clean;
};

const CommodityExpiry: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id;

  // ── Data state ──
  const [trades, setTrades] = useState<Record<string, any>[]>([]);
  const [groups, setGroups] = useState<MaturedTradeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Date filter (same pattern as ForwardMaturity) ──
  const [selectedDate, setSelectedDate] = useState('');
  const [pendingDate, setPendingDate] = useState('');

  // ── Filters ──
  const [selectedCounterparties, setSelectedCounterparties] = useState<Set<string>>(new Set());
  const [selectedFixingRefs, setSelectedFixingRefs] = useState<Set<string>>(new Set());

  // ── Inline price inputs (keyed by fixingReference) ──
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [savedPrices, setSavedPrices] = useState<Record<string, number>>({});

  // ── Fixing status ──
  const [fixingStatus, setFixingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [fixingMessage, setFixingMessage] = useState('');

  // ── Toast ──
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // ── System date ──
  const systemDateResult = useSystemDate(clientId);
  const dateInitialized = useRef(false);

  useEffect(() => {
    if (systemDateResult.systemDate && !dateInitialized.current) {
      dateInitialized.current = true;
      setSelectedDate(systemDateResult.systemDate);
      setPendingDate(systemDateResult.systemDate);
    }
  }, [systemDateResult.systemDate]);

  // ── Fetch matured trades ──
  const fetchData = useCallback(async (date: string, silent = false) => {
    if (!clientId || !date) return;

    try {
      if (!silent) setLoading(true);
      setError(null);

      const resp = await commodityFixingService.getMaturedTrades(clientId, date);
      if (resp.success && resp.data) {
        setTrades(resp.data.trades || []);
        setGroups(resp.data.groups || []);

        // Populate price inputs from fetched data
        const inputs: Record<string, string> = {};
        const saved: Record<string, number> = {};
        const lang = i18n.language?.substring(0, 2);
        const useComma = lang === 'es' || lang === 'pt';

        for (const g of resp.data.groups) {
          if (g.available && g.settlementPrice != null) {
            saved[g.fixingReference] = g.settlementPrice;
            inputs[g.fixingReference] = useComma
              ? String(g.settlementPrice).replace('.', ',')
              : String(g.settlementPrice);
          } else {
            inputs[g.fixingReference] = '';
          }
        }
        setPriceInputs(inputs);
        setSavedPrices(saved);
      } else {
        setError(resp.message || t('commodities.fixing.errorLoadMatured'));
      }
    } catch {
      setError(t('commodities.fixing.errorLoadMatured'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    if (selectedDate) fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  // ── Date handlers ──
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingDate(e.target.value);
  };

  const handleDateApply = () => {
    if (!pendingDate || pendingDate === selectedDate) return;
    setSelectedDate(pendingDate);
    setSelectedCounterparties(new Set());
    setSelectedFixingRefs(new Set());
    setFixingStatus('idle');
    setFixingMessage('');
  };

  // ── Filter helpers ──
  const uniqueCounterparties = useMemo(() => {
    return Array.from(new Set(trades.map(t => t.CounterpartyName).filter(Boolean))).sort();
  }, [trades]);

  const uniqueFixingRefs = useMemo(() => {
    return Array.from(new Set(trades.map(t => t.FixingReference).filter(Boolean))).sort();
  }, [trades]);

  const filteredTrades = useMemo(() => {
    return trades.filter(trade => {
      if (selectedCounterparties.size > 0 && !selectedCounterparties.has(trade.CounterpartyName)) return false;
      if (selectedFixingRefs.size > 0 && !selectedFixingRefs.has(trade.FixingReference)) return false;
      return true;
    });
  }, [trades, selectedCounterparties, selectedFixingRefs]);

  const toggleFilter = (set: Set<string>, value: string, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const handleClearFilters = () => {
    setSelectedCounterparties(new Set());
    setSelectedFixingRefs(new Set());
  };

  // ── Run Fixing (save prices first, then trigger fixing) ──
  const handleRunFixing = async () => {
    if (!clientId || !selectedDate) return;

    // Validate manually-entered prices
    const pricesToSave: { fixingReference: string; price: number; currency: string }[] = [];
    for (const g of groups) {
      if (savedPrices[g.fixingReference] != null) continue; // Already saved
      const input = priceInputs[g.fixingReference] || '';
      if (!input) continue; // Empty — skip

      const num = parseAmountInput(input);
      if (num === null || !isFinite(num) || num <= 0) {
        setFixingStatus('error');
        setFixingMessage(t('commodities.fixing.errorSavePrices'));
        return;
      }
      pricesToSave.push({
        fixingReference: g.fixingReference,
        price: num,
        currency: g.currency,
      });
    }

    setFixingStatus('loading');
    setFixingMessage('');

    try {
      // Save any manually-entered prices (per group, since maturity dates may differ)
      if (pricesToSave.length > 0) {
        for (const p of pricesToSave) {
          const group = groups.find(g => g.fixingReference === p.fixingReference);
          if (!group) continue;

          const resp = await commodityValuationService.savePrices(
            clientId,
            group.maturityDate,
            [p],
          );
          if (!resp.success) {
            setFixingStatus('error');
            setFixingMessage(t('commodities.fixing.errorSavePrices'));
            return;
          }

          // Mark as saved
          const lang = i18n.language?.substring(0, 2);
          const useComma = lang === 'es' || lang === 'pt';
          setSavedPrices(prev => ({ ...prev, [p.fixingReference]: p.price }));
          setPriceInputs(prev => ({
            ...prev,
            [p.fixingReference]: useComma
              ? String(p.price).replace('.', ',')
              : String(p.price),
          }));
        }
      }

      // Run fixing
      const fixResp = await commodityFixingService.runFixing(clientId, selectedDate);
      if (!fixResp.success) {
        setFixingStatus('error');
        setFixingMessage(t('commodities.fixing.errorRunFixing'));
        return;
      }

      const fixed = fixResp.data?.tradesFixed ?? 0;
      const skipped = fixResp.data?.tradesSkipped ?? 0;
      const msg =
        `${fixed} ${t('commodities.fixing.tradesFixed')}` +
        (skipped > 0 ? `, ${skipped} ${t('commodities.fixing.tradesSkipped')}` : '');
      setFixingStatus('success');
      setFixingMessage(msg);
      setToast({ message: msg, type: 'success' });

      // Refresh — trades remain visible (now with settlement fields populated)
      await fetchData(selectedDate, true);
    } catch {
      setFixingStatus('error');
      setFixingMessage(t('commodities.fixing.errorRunFixing'));
    }
  };

  // ── Render ──
  return (
    <div className="commodity-expiry-page">
      {/* Header */}
      <div className="commodity-expiry-header">
        <div className="commodity-expiry-title">{t('commodities.fixing.title')}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Inline fixing rates panel */}
          {groups.length > 0 && (
            <div className="ce-fixing-rates">
              {groups.map((g, idx) => (
                <React.Fragment key={g.fixingReference}>
                  {idx > 0 && <span className="ce-separator">|</span>}
                  <span className="ce-rate-label">{g.fixingReference}</span>
                  <input
                    type="text"
                    value={priceInputs[g.fixingReference] || ''}
                    onChange={(e) => {
                      if (savedPrices[g.fixingReference] != null) return;
                      setPriceInputs(prev => ({
                        ...prev,
                        [g.fixingReference]: sanitizeDecimalInput(e.target.value, 4),
                      }));
                    }}
                    readOnly={savedPrices[g.fixingReference] != null}
                    className={`ce-rate-input ${savedPrices[g.fixingReference] != null ? 'readonly' : 'editable'}`}
                    placeholder=""
                  />
                </React.Fragment>
              ))}

              <button
                className="ce-run-fixing-btn"
                onClick={handleRunFixing}
                disabled={fixingStatus === 'loading'}
              >
                {fixingStatus === 'loading'
                  ? t('commodities.fixing.running')
                  : t('commodities.fixing.runFixing')
                }
              </button>

              {fixingMessage && (
                <span className={`ce-status-message ${fixingStatus}`}>
                  {fixingMessage}
                </span>
              )}
            </div>
          )}

          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
        </div>
      </div>

      {/* Grid panel */}
      <div className="commodity-expiry-panel">
        <div className="commodity-expiry-grid-header">
          <h3>{t('commodities.fixing.maturedTrades')}</h3>
          <div className="commodity-expiry-filters">
            {/* Date filter */}
            <div className="filter-group">
              <label>{t('commodities.fixing.targetDate')}:</label>
              <input
                type="date"
                value={pendingDate}
                onChange={handleDateChange}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDateApply(); }}
                className="date-filter-input"
              />
              {pendingDate !== selectedDate && pendingDate && (
                <button
                  onClick={handleDateApply}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '4px',
                    color: '#e2e8f0',
                    padding: '2px 10px',
                    fontSize: '12px',
                    height: '28px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  OK
                </button>
              )}
            </div>

            {/* Counterparty filter */}
            {uniqueCounterparties.length > 0 && (
              <div className="filter-group">
                <label>{t('commodities.fixing.counterparty')}:</label>
                <div className="filter-buttons">
                  {uniqueCounterparties.map(cpty => (
                    <button
                      key={cpty}
                      onClick={() => toggleFilter(selectedCounterparties, cpty, setSelectedCounterparties)}
                      className={`filter-btn ${selectedCounterparties.has(cpty) ? 'active' : ''}`}
                    >
                      {cpty}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Fixing Reference filter */}
            {uniqueFixingRefs.length > 1 && (
              <div className="filter-group">
                <label>{t('commodities.fixing.fixingReference')}:</label>
                <div className="filter-buttons">
                  {uniqueFixingRefs.map(ref => (
                    <button
                      key={ref}
                      onClick={() => toggleFilter(selectedFixingRefs, ref, setSelectedFixingRefs)}
                      className={`filter-btn ${selectedFixingRefs.has(ref) ? 'active' : ''}`}
                    >
                      {ref}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleClearFilters}
              className="action-btn clear-btn"
            >
              {t('commodities.fixing.clearFilters')}
            </button>
            <button
              onClick={() => fetchData(selectedDate)}
              disabled={loading}
              className={`action-btn refresh-btn ${loading ? 'loading' : ''}`}
            >
              {loading ? t('commodities.fixing.refreshing') : t('commodities.fixing.refresh')}
            </button>
          </div>
        </div>

        <div className="commodity-expiry-grid-container">
          {loading ? (
            <div className="grid-loading">
              <p>{t('commodities.fixing.loading')}</p>
            </div>
          ) : error ? (
            <div className="grid-error">
              <p>{error}</p>
            </div>
          ) : filteredTrades.length === 0 ? (
            <div className="commodity-expiry-empty">
              <p>{t('commodities.fixing.noMaturedTrades')}</p>
            </div>
          ) : (
            <CommodityTradesGrid
              trades={filteredTrades}
              loading={false}
              showSettlementColumns={true}
            />
          )}
        </div>
      </div>

      {/* Fixing overlay */}
      {fixingStatus === 'loading' && (
        <div className="ce-fixing-overlay">
          <div className="ce-fixing-overlay-content">
            <div className="spinner" />
            <span className="ce-fixing-overlay-label">
              {t('commodities.fixing.running')}
            </span>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CommodityExpiry;
